import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message, Spin } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import { useAuth } from '../../contexts/AuthContext';
import {
  experimentRecordService,
  ExperimentDiscipline,
  ExperimentRecord,
  ExperimentStatus,
  RelatedReservationSummary
} from '../../services/experimentRecords/experimentRecordService';
import { getDemoExperimentRecordById, isDemoExperimentRecordId } from '../../demo/demoBrowseMocks';
import {
  coerceReagentRows,
  formatReservationChipSubtitle,
  formatReservationSlot,
  instrumentNameFromReservation,
  normalizeRelatedLitFromRecord,
  normalizeRelatedResFromRecord
} from './experimentRecordShared';
import '../../styles/literature-stitch.css';
import '../../styles/experiment-records-stitch.css';
import '../MemberManagement.css';

const DISCIPLINE_LABEL: Record<ExperimentDiscipline, string> = {
  chemistry: '化学',
  biology: '生物',
  materials: '材料',
  general: '综合'
};

const STATUS_LABEL: Record<ExperimentStatus, string> = {
  draft: '草稿',
  final: '已定稿'
};

function displayUser(u: { name?: string; username?: string; email?: string } | undefined): string {
  if (!u) return '—';
  const s = (u.name || u.username || u.email || '').trim();
  return s || '—';
}

function formatAttachmentSize(bytes: number): string {
  if (bytes == null || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function sanitizeDownloadFilename(name: string): string {
  const base = (name || 'attachment').trim() || 'attachment';
  return base.replace(/[/\\:*?"<>|]/g, '_');
}

function resolveAttachmentHref(href: string): string {
  const u = (href || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('//')) return `${window.location.protocol}${u}`;
  if (u.startsWith('/')) return `${window.location.origin}${u}`;
  return `${window.location.origin}/${u.replace(/^\//, '')}`;
}

function isSameOriginAbsoluteUrl(absolute: string): boolean {
  try {
    return new URL(absolute).origin === window.location.origin;
  } catch {
    return false;
  }
}

async function downloadCrossOriginAttachment(absolute: string, fileName: string): Promise<void> {
  try {
    const res = await fetch(absolute, { mode: 'cors' });
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch {
    message.warning('该附件无法跨域直接下载，已在新窗口打开，请在浏览器中使用「另存为」。');
    window.open(absolute, '_blank', 'noopener,noreferrer');
  }
}

function litSubtitle(r: { authors?: string[]; journal?: string; year?: number }): string {
  const bits: string[] = [];
  if (r.authors?.length) bits.push(r.authors.slice(0, 2).join(', '));
  if (r.journal) bits.push(r.journal);
  if (r.year != null) bits.push(String(r.year));
  return bits.join(' · ');
}

function resSubtitle(r: RelatedReservationSummary): string {
  const slot = formatReservationSlot(r);
  const purpose = (r.purpose || '').trim();
  if (purpose && slot) return `${purpose} · ${slot}`;
  if (purpose) return purpose;
  if (slot) return slot;
  return formatReservationChipSubtitle(r);
}

const ExperimentRecordDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDemoBrowseOnly } = useAuth();

  const [loading, setLoading] = useState(true);
  const [rec, setRec] = useState<ExperimentRecord | null>(null);
  const [canEditPage, setCanEditPage] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      if (isDemoBrowseOnly && isDemoExperimentRecordId(id)) {
        const demo = getDemoExperimentRecordById(id);
        if (!demo) {
          message.error('未找到该演示记录');
          navigate('/workbench/experiment-records');
          return;
        }
        setRec(demo);
        setCanEditPage(false);
        return;
      }
      const { experimentRecord, canEdit } = await experimentRecordService.getById(id);
      setRec(experimentRecord);
      setCanEditPage(Boolean(canEdit) && !isDemoBrowseOnly);
    } catch (e: unknown) {
      message.error((e as Error).message || '加载失败');
      navigate('/workbench/experiment-records');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, isDemoBrowseOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  const relatedRes = useMemo(() => (rec ? normalizeRelatedResFromRecord(rec) : []), [rec]);
  const relatedLit = useMemo(() => (rec ? normalizeRelatedLitFromRecord(rec) : []), [rec]);
  const reagentRows = useMemo(() => (rec ? coerceReagentRows(rec.chemistry) : []), [rec]);
  const hasReagentData = useMemo(
    () => reagentRows.some((row) => row.compound.trim() || row.molarity.trim() || row.volume.trim()),
    [reagentRows]
  );

  const chem = rec?.chemistry;

  return (
    <div className="page-container full-height members-lims literature-lims-module er-page">
      <nav className="er-notebook-crumb" aria-label="breadcrumb">
        <button type="button" className="er-notebook-crumb-link" onClick={() => navigate('/')}>
          首页
        </button>
        <span className="material-symbols-outlined" aria-hidden>
          chevron_right
        </span>
        <button type="button" className="er-notebook-crumb-link" onClick={() => navigate('/workbench')}>
          工作台
        </button>
        <span className="material-symbols-outlined" aria-hidden>
          chevron_right
        </span>
        <button type="button" className="er-notebook-crumb-link" onClick={() => navigate('/workbench/experiment-records')}>
          实验记录本
        </button>
        <span className="material-symbols-outlined" aria-hidden>
          chevron_right
        </span>
        <span className="er-notebook-crumb-current">笔记详情</span>
      </nav>

      <Spin spinning={loading}>
        {rec ? (
          <>
            <header className="er-notebook-hero" style={{ marginBottom: '1rem' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 className="er-notebook-hero-title" style={{ wordBreak: 'break-word' }}>
                  {rec.title}
                </h1>
                <p className="er-notebook-hero-sub">
                  {DISCIPLINE_LABEL[rec.discipline]} · {STATUS_LABEL[rec.status]}
                  {rec.createdAt
                    ? ` · 创建于 ${new Date(rec.createdAt).toLocaleString('zh-CN', { dateStyle: 'medium', timeStyle: 'short' })}`
                    : ''}
                </p>
                {rec.tags?.length ? (
                  <div className="er-tags-cloud er-tags-cloud--readonly" style={{ marginTop: '0.75rem' }}>
                    {rec.tags.map((t) => (
                      <span key={t} className="er-tag-chip er-tag-chip--readonly">
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="er-notebook-hero-actions">
                <button type="button" className="er-btn-filter-ghost" onClick={() => navigate('/workbench/experiment-records')}>
                  返回列表
                </button>
                {canEditPage ? (
                  <button type="button" className="mm-btn-primary" onClick={() => navigate(`/workbench/experiment-records/${rec._id}/edit`)}>
                    <LimsNavIcon name="edit_note" sizePx={18} />
                    编辑
                  </button>
                ) : null}
              </div>
            </header>

            <div className="er-detail-meta" aria-label="记录元信息">
              <div className="er-detail-meta-item">
                <span className="er-detail-meta-k">创建人</span>
                <span className="er-detail-meta-v">{displayUser(rec.createdBy)}</span>
              </div>
              <div className="er-detail-meta-item">
                <span className="er-detail-meta-k">最近更新</span>
                <span className="er-detail-meta-v">
                  {rec.updatedAt ? new Date(rec.updatedAt).toLocaleString('zh-CN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                </span>
              </div>
              <div className="er-detail-meta-item">
                <span className="er-detail-meta-k">最后编辑</span>
                <span className="er-detail-meta-v">{displayUser(rec.updatedBy)}</span>
              </div>
            </div>

            <div className="er-detail-body">
              <section className="er-card-stitch">
                <div className="er-card-stitch-head">
                  <span className="material-symbols-outlined" aria-hidden>
                    sticky_note_2
                  </span>
                  <h2 className="er-card-stitch-title">摘要</h2>
                </div>
                <div className="er-notebook-snippet-box er-notebook-snippet-box--detail">
                  <p>{(rec.summary || '').trim() || '（无摘要）'}</p>
                </div>
              </section>

              <section className="er-card-stitch">
                <div className="er-card-stitch-head">
                  <span className="material-symbols-outlined" aria-hidden>
                    link
                  </span>
                  <h2 className="er-card-stitch-title">关联数据</h2>
                </div>
                {relatedRes.length === 0 && relatedLit.length === 0 ? (
                  <p className="er-hint er-hint--block">未关联仪器预约与文献。</p>
                ) : (
                  <>
                    {relatedRes.length ? (
                      <div className="er-detail-subblock">
                        <h3 className="er-detail-subtitle">仪器预约</h3>
                        {relatedRes.map((r) => (
                          <div key={r._id} className="er-link-row er-link-row--detail">
                            <div className="er-link-row-icon">
                              <span className="material-symbols-outlined">precision_manufacturing</span>
                            </div>
                            <div className="er-link-row-body">
                              <p className="er-link-row-title">{instrumentNameFromReservation(r)}</p>
                              <p className="er-link-row-sub">{resSubtitle(r)}</p>
                            </div>
                            <Link className="er-rel-link" to="/instruments">
                              仪器
                            </Link>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {relatedLit.length ? (
                      <div className="er-detail-subblock">
                        <h3 className="er-detail-subtitle">参考文献</h3>
                        {relatedLit.map((r) => (
                          <div key={r._id} className="er-link-row er-link-row--detail">
                            <div className="er-link-row-icon">
                              <span className="material-symbols-outlined">article</span>
                            </div>
                            <div className="er-link-row-body">
                              <Link className="er-rel-link er-rel-link--block" to={`/literature/${r._id}`}>
                                {r.title.length > 120 ? `${r.title.slice(0, 120)}…` : r.title}
                              </Link>
                              {litSubtitle(r) ? <p className="er-link-row-sub">{litSubtitle(r)}</p> : null}
                            </div>
                            <Link className="er-rel-link" to={`/literature/${r._id}`}>
                              打开
                            </Link>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </section>

              {(rec.procedureNotes || '').trim() ? (
                <section className="er-card-stitch">
                  <div className="er-card-stitch-head">
                    <span className="material-symbols-outlined" aria-hidden>
                      checklist
                    </span>
                    <h2 className="er-card-stitch-title">实验流程与操作备注</h2>
                  </div>
                  <div className="er-readonly-prose">{(rec.procedureNotes || '').trim()}</div>
                </section>
              ) : null}

              {rec.discipline === 'chemistry' ? (
                <section className="er-card-stitch">
                  <div className="er-card-stitch-head">
                    <span className="material-symbols-outlined" aria-hidden>
                      science
                    </span>
                    <h2 className="er-card-stitch-title">化学实验模板</h2>
                  </div>

                  <div className="er-detail-kv">
                    <span className="er-detail-kv-label">实验目的</span>
                    <div className="er-readonly-prose">{(chem?.objective || '').trim() || '（未填写）'}</div>
                  </div>

                  {(chem?.procedureOutline || '').trim() ? (
                    <div className="er-detail-kv er-detail-kv--spaced">
                      <span className="er-detail-kv-label">实验步骤概要</span>
                      <div className="er-readonly-prose">{(chem?.procedureOutline || '').trim()}</div>
                    </div>
                  ) : null}

                  <div className="er-detail-kv er-detail-kv--spaced">
                    <span className="er-detail-kv-label">试剂与溶剂</span>
                    {hasReagentData ? (
                      <div className="er-table-wrap er-table-wrap--readonly">
                        <table className="er-table-lab">
                          <thead>
                            <tr>
                              <th>化合物 / 试剂</th>
                              <th>摩尔浓度</th>
                              <th>体积</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reagentRows
                              .filter((row) => row.compound.trim() || row.molarity.trim() || row.volume.trim())
                              .map((row, idx) => (
                                <tr key={`${row.compound}-${idx}`}>
                                  <td className="er-compound-cell">{row.compound || '—'}</td>
                                  <td>{row.molarity || '—'}</td>
                                  <td>{row.volume || '—'}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="er-hint er-hint--block">（未填写结构化试剂表）</p>
                    )}
                  </div>

                  {(chem?.conditions || '').trim() ? (
                    <div className="er-detail-kv er-detail-kv--spaced">
                      <span className="er-detail-kv-label">反应条件</span>
                      <div className="er-readonly-prose">{(chem?.conditions || '').trim()}</div>
                    </div>
                  ) : null}

                  {(chem?.observations || '').trim() ? (
                    <div className="er-detail-kv er-detail-kv--spaced">
                      <span className="er-detail-kv-label">过程监测与现象</span>
                      <div className="er-readonly-prose">{(chem?.observations || '').trim()}</div>
                    </div>
                  ) : null}

                  {(chem?.workupAndAnalysis || '').trim() ? (
                    <div className="er-detail-kv er-detail-kv--spaced">
                      <span className="er-detail-kv-label">后处理与分析</span>
                      <div className="er-readonly-prose">{(chem?.workupAndAnalysis || '').trim()}</div>
                    </div>
                  ) : null}
                </section>
              ) : null}

              {rec.customFields && rec.customFields.length > 0 ? (
                <section className="er-card-stitch">
                  <div className="er-card-stitch-head">
                    <span className="material-symbols-outlined" aria-hidden>
                      tune
                    </span>
                    <h2 className="er-card-stitch-title">自定义字段</h2>
                  </div>
                  <p className="er-hint" style={{ marginTop: '-0.35rem' }}>
                    历史数据字段，新建记录请使用正文或化学模板表述。
                  </p>
                  <div className="er-detail-dl">
                    {rec.customFields.map((row, idx) => (
                      <div key={`cf-${idx}`} className="er-detail-dl-row">
                        <span className="er-detail-dl-k">{row.key || '（未命名）'}</span>
                        <span className="er-detail-dl-v">{row.value || '—'}</span>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              <section className="er-card-stitch">
                <div className="er-card-stitch-head">
                  <span className="material-symbols-outlined" aria-hidden>
                    article
                  </span>
                  <h2 className="er-card-stitch-title">正文</h2>
                </div>
                <div
                  className="er-rich-preview er-notebook-detail-preview"
                  dangerouslySetInnerHTML={{ __html: rec.content?.trim() ? rec.content : '<p>（暂无正文）</p>' }}
                />
              </section>

              {rec.attachments && rec.attachments.length > 0 ? (
                <section className="er-card-stitch">
                  <div className="er-card-stitch-head">
                    <span className="material-symbols-outlined" aria-hidden>
                      attach_file
                    </span>
                    <h2 className="er-card-stitch-title">附件</h2>
                  </div>
                  <ul className="er-detail-attach-list">
                    {rec.attachments.map((a) => {
                      const absolute = resolveAttachmentHref(a.url);
                      const safeName = sanitizeDownloadFilename(a.name);
                      const sameOrigin = isSameOriginAbsoluteUrl(absolute);
                      return (
                        <li key={a.url} className="er-detail-attach-row">
                          <span className="material-symbols-outlined er-detail-attach-icon" aria-hidden>
                            draft
                          </span>
                          <a
                            className="er-detail-attach-name"
                            href={absolute}
                            {...(sameOrigin ? { download: safeName } : {})}
                            onClick={(e) => {
                              if (!sameOrigin) {
                                e.preventDefault();
                                void downloadCrossOriginAttachment(absolute, safeName);
                              }
                            }}
                          >
                            {a.name}
                          </a>
                          <span className="er-detail-attach-meta">{formatAttachmentSize(a.size)}</span>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ) : null}
            </div>
          </>
        ) : null}
      </Spin>
    </div>
  );
};

export default ExperimentRecordDetail;
